import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { categories, products, projects } from '../data/sbbData';
import { ChevronRight, ShieldAlert, CheckCircle } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function SolutionCategoryDetail() {
  const { categorySlug } = useParams();
  const { lang, t } = useLanguage();

  const category = categories.find((c) => c.slug === categorySlug) || categories[0];

  // Filter products for this solution category
  const categoryProducts = products.filter(
    (p) => p.category === category.slug || category.slug === 'solusi-teraplikasi'
  );

  // Filter projects for this category
  const categoryProjects = projects.filter((proj) => proj.category === category.slug);

  return (
    <div className="category-detail-page">
      {/* Category Hero */}
      <PageHero eyebrow="Solusi Spesifik Sektor" title={lang === 'en' ? category.name_en : category.name} subtitle={category.description} image={category.image} />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><Link to="/produk-layanan">{t('nav_products')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{lang === 'en' ? category.name_en : category.name}</span></li>
          </ul>
        </div>
      </div>

      {/* Overview & Challenges Section */}
      <section className="section">
        <div className="container">
          <div className="grid-2 align-center">
            <div>
              <span className="accent-bar"></span>
              <span className="eyebrow">Tantangan & Kebutuhan Konstruksi</span>
              <h2 className="section-title">Memahami Karakteristik Sektor {category.name}</h2>
              <p className="about-desc-text">{category.description}</p>

              <div className="challenge-callout-box">
                <ShieldAlert size={24} color="var(--color-brand-primary)" className="icon-alert" />
                <div>
                  <strong>Tantangan Utama Lapangan:</strong>
                  <p>{category.challenges}</p>
                </div>
              </div>
            </div>

            <div>
              <img src={category.image} alt={category.name} className="img-rounded-shadow" />
            </div>
          </div>
        </div>
      </section>

      {/* Related Products Grid */}
      <section className="section section-subtle">
        <div className="container">
          <div className="section-header">
            <span className="accent-bar"></span>
            <span className="eyebrow">Produk Rekomendasi</span>
            <h2 className="section-title">Produk & Material Solusi {category.name}</h2>
          </div>

          <div className="grid-3">
            {categoryProducts.map((prod) => (
              <div key={prod.slug} className="sbb-card">
                <img src={prod.image} alt={prod.name} className="sbb-card-img" />
                <div className="sbb-card-body">
                  <span className="sbb-card-tag">{prod.categoryName}</span>
                  <h3 className="sbb-card-title">{prod.name}</h3>
                  <p className="sbb-card-desc">{prod.summary}</p>
                  <Link to={`/produk-layanan/detail/${prod.slug}`} className="btn btn-primary btn-sm">
                    <span>Detail Produk</span>
                    <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Masterpiece Projects */}
      {categoryProjects.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-header">
              <span className="accent-bar"></span>
              <span className="eyebrow">Rekam Jejak Teruji</span>
              <h2 className="section-title">Proyek Mahakarya Sektor {category.name}</h2>
            </div>

            <div className="grid-2">
              {categoryProjects.map((proj) => (
                <div key={proj.slug} className="sbb-card">
                  <img src={proj.image} alt={proj.name} className="sbb-card-img" />
                  <div className="sbb-card-body">
                    <span className="sbb-card-tag">{proj.location} • {proj.year}</span>
                    <h3 className="sbb-card-title">{proj.name}</h3>
                    <p className="sbb-card-desc">{proj.summary}</p>
                    <Link to={`/proyek/${proj.slug}`} className="btn btn-secondary btn-sm">
                      <span>Lihat Studi Kasus</span>
                      <ChevronRight size={14} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Contact */}
      <section className="cta-band-section">
        <div className="container text-center">
          <h2>Konsultasikan Kebutuhan Proyek {category.name} Anda</h2>
          <p style={{ marginTop: '8px', marginBottom: '24px', color: 'var(--color-text-muted)' }}>
            Tim spesialis teknikal kami siap merekomendasikan racikan beton paling presisi dan efisien.
          </p>
          <Link to="/kontak-kami" className="btn btn-primary">
            {t('contact_us_btn')}
          </Link>
        </div>
      </section>

      <style>{`
        .challenge-callout-box {
          display: flex;
          gap: 16px;
          background-color: var(--color-surface-subtle);
          padding: 20px;
          border-left: 4px solid var(--color-brand-primary);
          border-radius: var(--border-radius-sm);
          font-size: 14px;
        }

        .icon-alert {
          flex-shrink: 0;
          margin-top: 2px;
        }
      `}</style>
    </div>
  );
}
